import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import logo from "../logo/Legalcastlegroupnbg.png";
import iprLogo from "../logo/IPRIghts.png";
import businessLogo from "../logo/Bussinesslaw.png";
import bankingLogo from "../logo/Brandprotection.png";
import litigationLogo from "../logo/Litigation.png";

import countryHarvestLogo from "../logo/Countryharvest.png";
import blueDartLogo from "../logo/blue-dart.png";
import vistraBiotechLogo from "../logo/Vistra Biotech.png";

import {
  Paper,
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Slide,
  Zoom,
  Avatar,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import GavelIcon from "@mui/icons-material/Gavel";
import SecurityIcon from "@mui/icons-material/Security";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { keyframes } from "@mui/system";

/* ── Hero animations ── */
const fadeLeft = keyframes`
  from { opacity: 0; transform: translateX(-32px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const fadeRight = keyframes`
  from { opacity: 0; transform: translateX(32px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const breathe = keyframes`
  0%,100% { transform: translate(-50%,-50%) scale(1); opacity: 0.8; }
  50%     { transform: translate(-50%,-50%) scale(1.12); opacity: 1; }
`;
const levitate = keyframes`
  0%,100% { transform: translateY(0); }
  50%     { transform: translateY(-10px); }
`;
const marqueeScroll = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;
const pulseGlow = keyframes`
  0%,100% { box-shadow: 0 6px 30px rgba(70,130,180,0.4), inset 0 1px 0 rgba(255,255,255,0.35); }
  50%     { box-shadow: 0 6px 40px rgba(70,130,180,0.65), inset 0 1px 0 rgba(255,255,255,0.35); }
`;

interface PracticeArea {
  title: string;
  description: string;
  logo: string;
  link: string;
}

const practiceAreas: PracticeArea[] = [
  {
    title: "Intellectual Property Rights",
    description:
      "Protection of patents, trademarks, copyrights, Enforcement, Litigation and trade secrets to secure your innovations.",
    logo: iprLogo,
    link: "/practice-areas/intellectual-property",
  },
  {
    title: "Business Law",
    description:
      "Comprehensive legal solutions for business formation, contracts, Legal structuring and corporate advisory.",
    logo: businessLogo,
    link: "/practice-areas/business-law",
  },
  {
    title: "Banking law",
    description:
      "Legal guidance for regulatory complaince, loan recovery, and Banking disputes.",
    logo: bankingLogo,
    link: "/practice-areas/banking-law",
  },
  {
    title: "Litigation & Dispute Resolution",
    description:
      "Resolving disputes with precision—leveraging strategic advocacy, skillful negotiation, effective arbitration, and decisive court litigation.",
    logo: litigationLogo,
    link: "/practice-areas/litigation",
  },
];

const aboutHighlights = [
  {
    icon: <GavelIcon sx={{ fontSize: 40 }} />,
    text: "Over 10 Years of Proven Legal Expertise"
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    text: "Comprehensive Services in Business Law and Intellectual Property"
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
    text: "Focused on Delivering Results with a Client-Centered Approach"
  },
  {
    icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
    text: "Commitment to Providing Innovative and Customized Legal Solutions"
  },
  {
    icon: <VerifiedUserIcon sx={{ fontSize: 40 }} />,
    text: "Recognized for Excellence in Protecting Creative Works and Business Interests"
  },
];

const Home = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  return (
    <Box>
      {/* ═══════════ Hero Banner ═══════════ */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: { xs: "auto", md: "85vh" },
          display: "flex",
          alignItems: "center",
          background: "linear-gradient(160deg, #0D2139 0%, #0A1826 55%, #061018 100%)",
        }}
      >
        {/* Atmospheric radial layers */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            zIndex: 0,
            background: [
              "radial-gradient(ellipse 55% 80% at 0% 50%, rgba(70,130,180,0.22) 0%, transparent 60%)",
              "radial-gradient(ellipse 40% 50% at 50% 100%, rgba(176,196,222,0.07) 0%, transparent 60%)",
              "radial-gradient(ellipse 35% 60% at 100% 10%, rgba(44,95,138,0.3) 0%, transparent 55%)",
            ].join(", "),
          }}
        />

        {/* Diagonal geometric accents */}
        <Box
          sx={{
            position: "absolute",
            top: -60,
            right: "30%",
            width: 2,
            height: "160%",
            background:
              "linear-gradient(to bottom, transparent, rgba(176,196,222,0.08) 30%, rgba(176,196,222,0.08) 70%, transparent)",
            transform: "rotate(-12deg)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: -60,
            right: "calc(30% + 60px)",
            width: 1,
            height: "160%",
            background:
              "linear-gradient(to bottom, transparent, rgba(70,130,180,0.12) 30%, rgba(70,130,180,0.12) 70%, transparent)",
            transform: "rotate(-12deg)",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />

        {/* Corner marks */}
        {[
          { top: 14, left: 14, borderTop: "1px solid rgba(176,196,222,0.25)", borderLeft: "1px solid rgba(176,196,222,0.25)" },
          { top: 14, right: 14, borderTop: "1px solid rgba(176,196,222,0.25)", borderRight: "1px solid rgba(176,196,222,0.25)" },
          { bottom: 14, left: 14, borderBottom: "1px solid rgba(176,196,222,0.25)", borderLeft: "1px solid rgba(176,196,222,0.25)" },
          { bottom: 14, right: 14, borderBottom: "1px solid rgba(176,196,222,0.25)", borderRight: "1px solid rgba(176,196,222,0.25)" },
        ].map((s, i) => (
          <Box key={i} sx={{ position: "absolute", width: 24, height: 24, zIndex: 7, pointerEvents: "none", ...s }} />
        ))}

        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 5, pt: { xs: 10, md: 8 }, pb: { xs: 4, md: 0 } }}>
          <Grid
            container
            spacing={0}
            alignItems="center"
            sx={{ minHeight: { md: "85vh" } }}
          >
            {/* ── Left column ── */}
            <Grid size={{ xs: 12, md: 7 }}>
              <Box
                sx={{
                  animation: `${fadeLeft} 1s cubic-bezier(0.22,1,0.36,1) both`,
                  px: { xs: 2, md: 6 },
                  py: { xs: 4, md: 0 },
                }}
              >
                {/* Tag line */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 3,
                    animation: `${fadeLeft} 0.9s 0.1s cubic-bezier(0.22,1,0.36,1) both`,
                  }}
                >
                  <Box
                    sx={{
                      background: "rgba(70,130,180,0.18)",
                      border: "1px solid rgba(176,196,222,0.2)",
                      px: 1.75,
                      py: 0.6,
                      fontFamily: "'Cinzel', serif",
                      fontSize: "8px",
                      letterSpacing: "0.28em",
                      color: "#B0C4DE",
                      textTransform: "uppercase",
                    }}
                  >
                    Trusted Counsel
                  </Box>
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      bgcolor: "#4682B4",
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "'Cinzel', serif",
                      fontSize: "8px",
                      letterSpacing: "0.22em",
                      color: "rgba(176,196,222,0.5)",
                      textTransform: "uppercase",
                    }}
                  >
                    10+ Years of Legal Excellence
                  </Typography>
                </Box>

                {/* Headline */}
                <Box
                  sx={{
                    mb: 1.5,
                    animation: `${fadeLeft} 0.9s 0.2s cubic-bezier(0.22,1,0.36,1) both`,
                  }}
                >
                  <Typography
                    sx={{
                      display: "block",
                      fontFamily: "'Playfair Display', serif",
                      fontWeight: 400,
                      fontSize: { xs: "2.4rem", sm: "3rem", md: "clamp(44px, 5vw, 72px)" },
                      lineHeight: 1.05,
                      color: "#F8FBFF",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Your Business.
                  </Typography>
                  <Typography
                    sx={{
                      display: "block",
                      fontFamily: "'Playfair Display', serif",
                      fontWeight: 700,
                      fontStyle: "italic",
                      fontSize: { xs: "2.4rem", sm: "3rem", md: "clamp(44px, 5vw, 72px)" },
                      lineHeight: 1.05,
                      letterSpacing: "-0.01em",
                      background: "linear-gradient(120deg, #D6E4F0 0%, #4682B4 80%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Our
                  </Typography>
                  <Typography
                    sx={{
                      display: "block",
                      fontFamily: "'Playfair Display', serif",
                      fontWeight: 400,
                      fontSize: { xs: "2.4rem", sm: "3rem", md: "clamp(44px, 5vw, 72px)" },
                      lineHeight: 1.05,
                      color: "#F8FBFF",
                      letterSpacing: "-0.01em",
                      mb: 3.5,
                    }}
                  >
                    Protection.
                  </Typography>
                </Box>

                {/* Practice area pills */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: { xs: 1, md: 0 },
                    mb: 2.5,
                    animation: `${fadeLeft} 0.9s 0.35s cubic-bezier(0.22,1,0.36,1) both`,
                  }}
                >
                  {["IP Protection", "Business Law", "Litigation"].map((area, i) => (
                    <Box key={area} sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        sx={{
                          fontWeight: 300,
                          fontSize: 11,
                          letterSpacing: "0.18em",
                          color: "rgba(176,196,222,0.6)",
                          textTransform: "uppercase",
                        }}
                      >
                        {area}
                      </Typography>
                      {i < 2 && (
                        <Box
                          sx={{
                            width: 24,
                            height: "1px",
                            bgcolor: "rgba(70,130,180,0.4)",
                            mx: 1.5,
                          }}
                        />
                      )}
                    </Box>
                  ))}
                </Box>

                {/* Tagline */}
                <Typography
                  sx={{
                    fontWeight: 300,
                    fontSize: 14,
                    lineHeight: 1.85,
                    color: "rgba(176,196,222,0.42)",
                    maxWidth: 400,
                    mb: 4,
                    animation: `${fadeLeft} 0.9s 0.42s cubic-bezier(0.22,1,0.36,1) both`,
                  }}
                >
                  Strategic legal solutions to protect your innovations and help
                  your business grow with confidence.
                </Typography>

                {/* Running marquee */}
                <Box
                  sx={{
                    overflow: "hidden",
                    width: { xs: "280px", md: "420px" },
                    mb: 2,
                    position: "relative",
                    animation: `${fadeLeft} 0.9s 0.48s cubic-bezier(0.22,1,0.36,1) both`,
                    "&::before, &::after": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      width: 40,
                      zIndex: 1,
                      pointerEvents: "none",
                    },
                    "&::before": {
                      left: 0,
                      background: "linear-gradient(to right, #0A1626, transparent)",
                    },
                    "&::after": {
                      right: 0,
                      background: "linear-gradient(to left, #0A1626, transparent)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      whiteSpace: "nowrap",
                      animation: `${marqueeScroll} 18s linear infinite`,
                    }}
                  >
                    {[...Array(2)].map((_, i) => (
                      <Typography
                        key={i}
                        component="span"
                        sx={{
                          fontFamily: '"Cinzel", serif',
                          fontSize: "9px",
                          letterSpacing: "0.22em",
                          textTransform: "uppercase",
                          color: "rgba(176,196,222,0.35)",
                          pr: 4,
                        }}
                      >
                        IP Protection &nbsp;&bull;&nbsp; Business Law &nbsp;&bull;&nbsp; Trademark Filing &nbsp;&bull;&nbsp; Litigation &nbsp;&bull;&nbsp; Copyright &nbsp;&bull;&nbsp; Brand Protection &nbsp;&bull;&nbsp; Legal Advisory &nbsp;&bull;&nbsp;{" "}
                      </Typography>
                    ))}
                  </Box>
                </Box>

                {/* CTA buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    animation: `${fadeLeft} 0.9s 0.5s cubic-bezier(0.22,1,0.36,1) both`,
                  }}
                >
                  <Button
                    component={Link}
                    to="/contact"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      fontFamily: "'Cinzel', serif",
                      fontSize: { xs: "10px", md: "11px" },
                      letterSpacing: "0.2em",
                      textTransform: "uppercase",
                      color: "#0D2139",
                      background:
                        "linear-gradient(135deg, #D6E4F0 0%, #B0C4DE 50%, #8aaec8 100%)",
                      border: "none",
                      px: { xs: 3.5, md: 5.5 },
                      py: { xs: 1.8, md: 2.2 },
                      fontWeight: 600,
                      borderRadius: 0,
                      animation: `${pulseGlow} 2.5s ease-in-out infinite`,
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow:
                          "0 14px 40px rgba(70,130,180,0.65)",
                        background:
                          "linear-gradient(135deg, #E2ECF5 0%, #B0C4DE 50%, #8aaec8 100%)",
                        animation: "none",
                      },
                    }}
                  >
                    Schedule Free Consultation
                  </Button>

                  <Button
                    component={Link}
                    to="#practice-areas"
                    sx={{
                      fontFamily: "'Cinzel', serif",
                      fontSize: "9.5px",
                      letterSpacing: "0.2em",
                      textTransform: "uppercase",
                      color: "rgba(176,196,222,0.55)",
                      background: "transparent",
                      border: "1px solid rgba(176,196,222,0.18)",
                      px: { xs: 3, md: 3.75 },
                      py: 2,
                      borderRadius: 0,
                      transition: "color 0.3s, border-color 0.3s, background 0.3s",
                      "&:hover": {
                        color: "#D6E4F0",
                        borderColor: "rgba(176,196,222,0.45)",
                        background: "rgba(70,130,180,0.08)",
                      },
                    }}
                  >
                    Practice Areas
                  </Button>
                </Box>
              </Box>
            </Grid>

            {/* ── Right column ── */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: { md: 450 },
                  py: { xs: 6, md: 0 },
                  animation: `${fadeRight} 1s 0.15s cubic-bezier(0.22,1,0.36,1) both`,
                  "&::before": {
                    content: '""',
                    display: { xs: "none", md: "block" },
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: "1px",
                    background: "rgba(176,196,222,0.08)",
                  },
                }}
              >
                {/* Glow */}
                <Box
                  sx={{
                    position: "absolute",
                    width: 280,
                    height: 280,
                    borderRadius: "50%",
                    background:
                      "radial-gradient(circle, rgba(70,130,180,0.18) 0%, transparent 65%)",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%,-50%)",
                    animation: `${breathe} 5s ease-in-out infinite`,
                    pointerEvents: "none",
                  }}
                />

                {/* Concentric circles */}
                {[320, 240, 160].map((size, i) => (
                  <Box
                    key={size}
                    sx={{
                      position: "absolute",
                      width: size,
                      height: size,
                      borderRadius: "50%",
                      border: "1px solid",
                      borderColor: i === 2
                        ? "rgba(70,130,180,0.12)"
                        : i === 1
                        ? "rgba(176,196,222,0.08)"
                        : "rgba(70,130,180,0.1)",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%,-50%)",
                      pointerEvents: "none",
                    }}
                  />
                ))}

                {/* Logo */}
                <Box
                  component="img"
                  src={logo}
                  alt="Legal Castle Group"
                  sx={{
                    position: "relative",
                    zIndex: 2,
                    width: 150,
                    height: 150,
                    objectFit: "contain",
                    filter:
                      "drop-shadow(0 0 18px rgba(176,196,222,0.5)) drop-shadow(0 0 60px rgba(70,130,180,0.3))",
                    animation: `${levitate} 7s ease-in-out infinite`,
                    mb: 3.5,
                  }}
                />

                {/* Firm name */}
                <Typography
                  sx={{
                    fontFamily: "'Cinzel', serif",
                    fontSize: 21,
                    fontWeight: 600,
                    letterSpacing: "0.26em",
                    color: "#D6E4F0",
                    textAlign: "center",
                    lineHeight: 1.3,
                    mb: 1.25,
                    textShadow: "0 0 30px rgba(70,130,180,0.4)",
                    zIndex: 2,
                    position: "relative",
                  }}
                >
                  LEGAL CASTLE
                  <br />
                  GROUP
                </Typography>

                {/* Decorative rule */}
                <Box
                  sx={{
                    width: 50,
                    height: "1px",
                    background:
                      "linear-gradient(to right, transparent, #4682B4, transparent)",
                    mb: 1.25,
                    zIndex: 2,
                    position: "relative",
                  }}
                />

                {/* Slogan */}
                <Typography
                  sx={{
                    fontFamily: "'Playfair Display', serif",
                    fontStyle: "italic",
                    fontSize: 12,
                    letterSpacing: "0.2em",
                    color: "rgba(176,196,222,0.45)",
                    textAlign: "center",
                    textTransform: "uppercase",
                    zIndex: 2,
                    position: "relative",
                  }}
                >
                  Satyasya Durgam
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* About Us Section */}
      <Box sx={{ bgcolor: "#F2F7FC", pt: { xs: 6, md: 10 }, pb: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Slide direction="right" in timeout={1000}>
                <Box>
                  {/* Section Header — inline with text */}
                  <Box sx={{ mb: 2.5 }}>
                    <Typography
                      sx={{
                        fontFamily: '"Cinzel", serif',
                        fontSize: "10px",
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                        color: "#4682B4",
                        mb: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        "&::before": {
                          content: '""',
                          width: 32,
                          height: 1,
                          background: "#4682B4",
                        },
                      }}
                    >
                      Who We Are
                    </Typography>
                    <Typography
                      variant="h2"
                      sx={{
                        fontFamily: '"Playfair Display", serif',
                        fontWeight: 400,
                        fontSize: { xs: "1.8rem", md: "2.6rem" },
                        color: "#0B1F36",
                        "& em": { fontStyle: "italic", color: "#4682B4" },
                      }}
                      dangerouslySetInnerHTML={{ __html: 'About <em>Our Firm</em>' }}
                    />
                  </Box>

                  <Typography
                    paragraph
                    sx={{ fontSize: "1.15rem", lineHeight: 1.8, color: "text.secondary", mb: 3 }}
                  >
                    At Legal Castle Group, we are dedicated to protecting the
                    innovative spirit of businesses, artists, and entrepreneurs.
                    With over a decade of experience in the legal field, we focus on
                    safeguarding Intellectual Property Rights and providing
                    comprehensive Business Law support.
                  </Typography>
                  <Typography
                    paragraph
                    sx={{ fontSize: "1.15rem", lineHeight: 1.8, color: "text.secondary" }}
                  >
                    Founded by{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: "primary.main" }}>
                      Chandrakant Kushwaha
                    </Box>
                    , a seasoned advocate with a special focus on Intellectual
                    Property Rights, our firm is built on a strong foundation of
                    trust, dedication, and unparalleled legal expertise.
                  </Typography>
                </Box>
              </Slide>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Grid container spacing={3} justifyContent="center">
                {aboutHighlights.map((highlight, index) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={index}>
                    <Zoom in timeout={1000 + index * 100} style={{ transitionDelay: `${index * 100}ms` }}>
                      <Card
                        elevation={0}
                        sx={{
                          height: "100%",
                          border: "1px solid rgba(70,130,180,0.25)",
                          borderRadius: 0,
                          position: "relative",
                          overflow: "hidden",
                          transition: "all 0.4s ease",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "3px",
                            background: "linear-gradient(90deg, #4682B4, #2C5F8A)",
                            transform: "scaleX(0)",
                            transformOrigin: "left",
                            transition: "transform 0.4s ease",
                          },
                          "&:hover": {
                            transform: "translateY(-6px)",
                            boxShadow: "0 16px 32px rgba(11, 31, 54, 0.1)",
                            "&::before": { transform: "scaleX(1)" },
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3, textAlign: "center" }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              mx: "auto",
                              mb: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "linear-gradient(135deg, rgba(70,130,180,0.12), rgba(44,95,138,0.08))",
                              border: "1px solid rgba(70,130,180,0.15)",
                              color: "#4682B4",
                            }}
                          >
                            {highlight.icon}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 400, lineHeight: 1.6, fontSize: "0.9rem" }}
                          >
                            {highlight.text}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Zoom>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Practice Areas Section */}
      <Box sx={{ bgcolor: "#FFFFFF", py: { xs: 6, md: 10 } }} id="practice-areas">
        <Container maxWidth="xl">
          {/* Section Header */}
          <Box sx={{ mb: 6, textAlign: "center" }}>
            <Typography
              sx={{
                fontFamily: '"Cinzel", serif',
                fontSize: "10px",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "#4682B4",
                mb: 1.5,
                display: "inline-flex",
                alignItems: "center",
                gap: 1.5,
                "&::before": {
                  content: '""',
                  width: 32,
                  height: 1,
                  background: "#4682B4",
                },
                "&::after": {
                  content: '""',
                  width: 32,
                  height: 1,
                  background: "#4682B4",
                },
              }}
            >
              What We Do
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 400,
                fontSize: { xs: "1.8rem", md: "2.6rem" },
                color: "#0B1F36",
                "& em": { fontStyle: "italic", color: "#4682B4" },
              }}
              dangerouslySetInnerHTML={{ __html: 'Our Practice <em>Areas</em>' }}
            />
            <Typography
              sx={{
                mt: 2,
                fontSize: "1rem",
                fontWeight: 400,
                color: "text.secondary",
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              Comprehensive legal solutions across multiple domains, tailored to protect your interests
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {practiceAreas.map((area, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Slide direction="up" in timeout={1000 + index * 150} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Paper
                    component={Link}
                    to={area.link}
                    elevation={0}
                    sx={{
                      p: 4,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      textDecoration: "none",
                      borderRadius: 0,
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      border: "1px solid rgba(70,130,180,0.25)",
                      position: "relative",
                      overflow: "hidden",
                      bgcolor: "#F2F7FC",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "3px",
                        background: "linear-gradient(90deg, #4682B4, #2C5F8A)",
                        transform: "scaleX(0)",
                        transformOrigin: "left",
                        transition: "transform 0.4s ease",
                      },
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: "-40px",
                        right: "-40px",
                        width: "120px",
                        height: "120px",
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(70,130,180,0.06) 0%, transparent 70%)",
                        transition: "transform 0.5s ease, opacity 0.5s ease",
                        opacity: 0,
                      },
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(11, 31, 54, 0.1)",
                        "&::before": { transform: "scaleX(1)" },
                        "&::after": { opacity: 1, transform: "scale(1.5)" },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        mb: 3,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "linear-gradient(135deg, rgba(70,130,180,0.1), rgba(44,95,138,0.06))",
                        border: "1px solid rgba(70,130,180,0.15)",
                        p: 1.5,
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      <img
                        src={area.logo}
                        alt={area.title}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: '"Cinzel", serif',
                        fontSize: "9px",
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        color: "rgba(70,130,180,0.7)",
                        mb: 1,
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      Practice Area
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: '"Playfair Display", serif',
                        color: "#0B1F36",
                        fontWeight: 500,
                        mb: 2,
                        fontSize: "1.1rem",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      {area.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        lineHeight: 1.7,
                        color: "text.secondary",
                        fontWeight: 400,
                        position: "relative",
                        zIndex: 1,
                        mb: 2,
                        flex: 1,
                      }}
                    >
                      {area.description}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Cinzel", serif',
                        fontSize: "10px",
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        color: "#4682B4",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        position: "relative",
                        zIndex: 1,
                        mt: "auto",
                      }}
                    >
                      Learn More →
                    </Typography>
                  </Paper>
                </Slide>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Trusted By Section */}
      <Box sx={{ bgcolor: "#F2F7FC", py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 5, textAlign: "center" }}>
            <Typography
              sx={{
                fontFamily: '"Cinzel", serif',
                fontSize: "10px",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "#4682B4",
                mb: 1.5,
                display: "inline-flex",
                alignItems: "center",
                gap: 1.5,
                "&::before": {
                  content: '""',
                  width: 32,
                  height: 1,
                  background: "#4682B4",
                },
                "&::after": {
                  content: '""',
                  width: 32,
                  height: 1,
                  background: "#4682B4",
                },
              }}
            >
              Our Clients
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 400,
                fontSize: { xs: "1.6rem", md: "2.2rem" },
                color: "#0B1F36",
                "& em": { fontStyle: "italic", color: "#4682B4" },
              }}
              dangerouslySetInnerHTML={{ __html: 'Trusted By <em>Leading</em> Organizations' }}
            />
          </Box>

          <Grid container spacing={6} justifyContent="center" alignItems="center">
            {[
              { name: "Country Harvest", logo: countryHarvestLogo },
              { name: "Bluedart", logo: blueDartLogo },
              { name: "Vistra Biotech", logo: vistraBiotechLogo },
            ].map((client, index) => (
              <Grid key={index}>
                <Zoom in timeout={1000 + index * 200} style={{ transitionDelay: `${index * 100}ms` }}>
                  <Box
                    sx={{
                      filter: "grayscale(100%)",
                      opacity: 0.6,
                      transition: "all 0.4s ease",
                      p: 3,
                      height: 120,
                      minWidth: 200,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 0,
                      border: "1px solid rgba(70,130,180,0.25)",
                      bgcolor: "#FFFFFF",
                      "&:hover": {
                        filter: "none",
                        opacity: 1,
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 28px rgba(11, 31, 54, 0.08)",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={client.logo}
                      alt={client.name}
                      onError={(e) => {
                        const target = e.target as HTMLElement;
                        target.style.display = 'none';
                        const fallback = target.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                      sx={{
                        height: 80,
                        width: "auto",
                        maxWidth: 200,
                        objectFit: "contain",
                      }}
                    />
                    <Box
                      sx={{
                        display: 'none',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 80,
                        minWidth: 180,
                        border: '2px solid',
                        borderColor: 'primary.main',
                        borderRadius: 2,
                        px: 3,
                        py: 2,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'primary.main',
                          fontWeight: 700,
                          textAlign: 'center',
                          fontSize: '1.1rem',
                          fontFamily: 'Playfair Display',
                        }}
                      >
                        {client.name}
                      </Typography>
                    </Box>
                  </Box>
                </Zoom>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Team Section */}
      <Box
        sx={{
          color: "white",
          py: { xs: 6, md: 10 },
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(160deg, #081524 0%, #0D2139 40%, #0B1F36 100%)",
        }}
        id="team"
      >
        {/* Atmospheric gradient */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(ellipse 50% 50% at 50% 50%, rgba(70,130,180,0.06) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Section Header */}
          <Box sx={{ mb: 6, textAlign: "center" }}>
            <Typography
              sx={{
                fontFamily: '"Cinzel", serif',
                fontSize: "10px",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "rgba(176,196,222,0.6)",
                mb: 1.5,
                display: "inline-flex",
                alignItems: "center",
                gap: 1.5,
                "&::before": {
                  content: '""',
                  width: 32,
                  height: 1,
                  background: "rgba(176,196,222,0.3)",
                },
                "&::after": {
                  content: '""',
                  width: 32,
                  height: 1,
                  background: "rgba(176,196,222,0.3)",
                },
              }}
            >
              Our People
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 400,
                fontSize: { xs: "1.8rem", md: "2.6rem" },
                color: "#F8FBFF",
                "& em": { fontStyle: "italic", color: "#B0C4DE" },
              }}
              dangerouslySetInnerHTML={{ __html: 'Meet Our <em>Team</em>' }}
            />
            <Typography
              sx={{
                mt: 2,
                fontSize: "1rem",
                fontWeight: 300,
                color: "rgba(176,196,222,0.5)",
                maxWidth: "500px",
                mx: "auto",
              }}
            >
              Dedicated professionals committed to excellence in legal service
            </Typography>
          </Box>

          <Grid container spacing={4} justifyContent="center">
            {/* Founder Card */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Zoom in timeout={1000}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 5,
                    height: "100%",
                    borderRadius: 0,
                    bgcolor: "#F2F7FC",
                    border: "1px solid rgba(70,130,180,0.25)",
                    textAlign: "center",
                    transition: "all 0.4s ease",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "3px",
                      background: "linear-gradient(90deg, #4682B4, #2C5F8A)",
                      transform: "scaleX(0)",
                      transformOrigin: "left",
                      transition: "transform 0.4s ease",
                    },
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 20px 40px rgba(11, 31, 54, 0.15)",
                      "&::before": { transform: "scaleX(1)" },
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      mx: "auto",
                      mb: 3,
                      bgcolor: "primary.main",
                      fontSize: "3rem",
                      fontWeight: 700,
                      border: "4px solid",
                      borderColor: "#4682B4",
                    }}
                  >
                    CK
                  </Avatar>
                  <Typography
                    variant="h5"
                    color="primary.main"
                    gutterBottom
                    sx={{ fontWeight: 700, fontFamily: "Playfair Display" }}
                  >
                    Chandrakant Kushwaha
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="#4682B4"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Founder Partner & IPR Specialist
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ fontStyle: "italic", mb: 2 }}
                  >
                    "Defending innovation with passion and precision."
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Driving the firm's vision with over a decade of expertise in
                    intellectual property rights and strategic litigation.
                  </Typography>
                </Paper>
              </Zoom>
            </Grid>

            {/* Partner Card */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Zoom in timeout={1200}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 5,
                    height: "100%",
                    borderRadius: 0,
                    bgcolor: "#F2F7FC",
                    border: "1px solid rgba(70,130,180,0.25)",
                    textAlign: "center",
                    transition: "all 0.4s ease",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "3px",
                      background: "linear-gradient(90deg, #4682B4, #2C5F8A)",
                      transform: "scaleX(0)",
                      transformOrigin: "left",
                      transition: "transform 0.4s ease",
                    },
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: "0 20px 40px rgba(11, 31, 54, 0.15)",
                      "&::before": { transform: "scaleX(1)" },
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 120,
                      height: 120,
                      mx: "auto",
                      mb: 3,
                      bgcolor: "primary.main",
                      fontSize: "3rem",
                      fontWeight: 700,
                      border: "4px solid",
                      borderColor: "#4682B4",
                    }}
                  >
                    LT
                  </Avatar>
                  <Typography
                    variant="h5"
                    color="primary.main"
                    gutterBottom
                    sx={{ fontWeight: 700, fontFamily: "Playfair Display" }}
                  >
                    Legal Team
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    color="#4682B4"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Client Relations & Strategy
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    paragraph
                    sx={{ fontStyle: "italic", mb: 2 }}
                  >
                    "Building bridges between law and client vision."
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Ensuring every client receives personalized attention and strategic
                    guidance tailored to their business goals.
                  </Typography>
                </Paper>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Band */}
      <Box
        sx={{
          position: "relative",
          color: "#F8FBFF",
          py: { xs: 8, md: 12 },
          overflow: "hidden",
          background: "linear-gradient(160deg, #081524 0%, #0D2139 100%)",
        }}
      >
        {/* Atmospheric gradients */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(ellipse 60% 50% at 30% 50%, rgba(70,130,180,0.08) 0%, transparent 70%)," +
              "radial-gradient(ellipse 40% 60% at 70% 50%, rgba(176,196,222,0.04) 0%, transparent 60%)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
          <Typography
            sx={{
              fontFamily: '"Cinzel", serif',
              fontSize: "10px",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "rgba(176,196,222,0.5)",
              mb: 2,
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              "&::before": {
                content: '""',
                width: 32,
                height: 1,
                background: "rgba(176,196,222,0.3)",
              },
              "&::after": {
                content: '""',
                width: 32,
                height: 1,
                background: "rgba(176,196,222,0.3)",
              },
            }}
          >
            Get Started
          </Typography>

          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 400,
              fontSize: { xs: "1.8rem", md: "2.6rem" },
              mb: 3,
              color: "#F8FBFF",
              "& em": { fontStyle: "italic", color: "#B0C4DE" },
            }}
            dangerouslySetInnerHTML={{
              __html: 'Ready to Protect Your <em>Intellectual Property?</em>',
            }}
          />

          <Typography
            sx={{
              mb: 5,
              fontWeight: 300,
              color: "rgba(176,196,222,0.5)",
              fontSize: "1rem",
              maxWidth: 500,
              mx: "auto",
            }}
          >
            Schedule a free consultation with our experienced IP attorneys and business law experts today
          </Typography>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Button
              component={Link}
              to="/contact"
              variant="contained"
              sx={{
                fontFamily: '"Cinzel", serif',
                fontSize: "10px",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontWeight: 600,
                background: "linear-gradient(135deg, #4682B4, #2C5F8A)",
                color: "#F8FBFF",
                px: 5,
                py: 1.8,
                borderRadius: 0,
                boxShadow: "0 6px 20px rgba(70,130,180,0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #5a94c4, #3a6f9a)",
                  boxShadow: "0 8px 28px rgba(70,130,180,0.45)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              Get Started
            </Button>
            <Button
              component={Link}
              to="/practice-areas/intellectual-property"
              variant="outlined"
              sx={{
                fontFamily: '"Cinzel", serif',
                fontSize: "10px",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontWeight: 500,
                color: "rgba(176,196,222,0.7)",
                borderColor: "rgba(176,196,222,0.25)",
                px: 5,
                py: 1.8,
                borderRadius: 0,
                "&:hover": {
                  borderColor: "#B0C4DE",
                  color: "#B0C4DE",
                  background: "rgba(176,196,222,0.06)",
                },
              }}
            >
              Learn More
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;


